/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";
import { SubscriptionPolicy } from "../../../data/interfaces";
import { Card, List, ListItem, Typography } from "@material-ui/core";
import { useStyles } from "./styles";

interface SubscriptionPolicyCardProps {
  subscriptionPolicy: SubscriptionPolicy | undefined;
  isSelected: boolean;
  handleSubPolicySelection?: (selectedSubPolicy: string) => void;
}

function SubscriptionPolicyCard(props: SubscriptionPolicyCardProps) {
  const { subscriptionPolicy, isSelected, handleSubPolicySelection } = props;
  const classes = useStyles();

  const getStopOnQuotaReachText = (stopOnQuotaReach: boolean) => {
    if (stopOnQuotaReach) {
      return (
        <>
          Stop on Quota Reach{" "}
          <Typography component="span" className={classes.policyCardTypography}>
            enabled
          </Typography>
        </>
      );
    }
    return (
      <>
        Stop on Quota Reach{" "}
        <Typography component="span" className={classes.policyCardTypography}>
          disabled
        </Typography>
      </>
    );
  };

  const getRateLimitText = (
    subscriptionPolicy: SubscriptionPolicy | undefined
  ) => {
    if (subscriptionPolicy && subscriptionPolicy.requestCount > 0) {
      return (
        <>
          <Typography component="span" className={classes.policyCardTypography}>
            {subscriptionPolicy.requestCount} requests per{" "}
            {subscriptionPolicy.timeUnit}
          </Typography>
        </>
      );
    } else if (subscriptionPolicy?.requestCount === -1) {
      return (
        <>
          <Typography component="span" className={classes.policyCardTypography}>
            Unlimited requests
          </Typography>
        </>
      );
    }
    return "";
  };

  const getBurstControllingText = (
    subscriptionPolicy: SubscriptionPolicy | undefined
  ) => {
    if (subscriptionPolicy && subscriptionPolicy.rateLimitCount > 0) {
      return (
        <>
          Burst Controlling{" "}
          <Typography component="span" className={classes.policyCardTypography}>
            applies{" "}
          </Typography>
          if request count exceeds{" "}
          <Typography component="span" className={classes.policyCardTypography}>
            {subscriptionPolicy.rateLimitCount} requests per{" "}
            {subscriptionPolicy.rateLimitTimeUnit}
          </Typography>
        </>
      );
    }
    return "";
  };

  const getApprovalText = (
    subscriptionPolicy: SubscriptionPolicy | undefined
  ) => {
    const text = isApprovalRequired(subscriptionPolicy)
      ? "Approval is required."
      : "Approval is not required";

    return (
      <>
        <Typography component="span" className={classes.policyCardTypography}>
          {text}
        </Typography>
      </>
    );
  };

  const isApprovalRequired = (
    subscriptionPolicy: SubscriptionPolicy | undefined
  ) => {
    return !!subscriptionPolicy?.attributes?.isApprovalRequired;
  };

  return (
    <Card
      className={`${classes.cardContent} ${
        isSelected ? classes.cardBorderSelected : classes.cardBorder
      }`}
      data-testid="subscription-policy-card"
      onClick={
        handleSubPolicySelection
          ? () => handleSubPolicySelection(subscriptionPolicy?.name ?? "")
          : undefined
      }
    >
      <Typography className={classes.cardHeaderTitle}>
        {subscriptionPolicy?.name}
      </Typography>

      <List>
        {isApprovalRequired(subscriptionPolicy) && (
          <ListItem className={classes.policyCardListItem}>
            <Typography>{getApprovalText(subscriptionPolicy)}</Typography>
          </ListItem>
        )}
        <ListItem className={classes.policyCardListItem}>
          <Typography>{getRateLimitText(subscriptionPolicy)}</Typography>
        </ListItem>
        {subscriptionPolicy?.description &&
          subscriptionPolicy?.description !== "" && (
            <ListItem className={classes.policyCardListItem}>
              <Typography>{subscriptionPolicy?.description}</Typography>
            </ListItem>
          )}
        <ListItem className={classes.policyCardListItem}>
          <Typography>
            {getStopOnQuotaReachText(
              subscriptionPolicy?.stopOnQuotaReach ?? false
            )}
          </Typography>
        </ListItem>
        {subscriptionPolicy?.rateLimitCount &&
          subscriptionPolicy?.rateLimitCount > 0 && 
          subscriptionPolicy.rateLimitCount !== 2147483647 && (
            <ListItem className={classes.policyCardListItem}>
              {getBurstControllingText(subscriptionPolicy)}
            </ListItem>
          )}
      </List>
    </Card>
  );
}

export default React.memo(SubscriptionPolicyCard);
